import { useState, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsBuilding, BsBoxArrowUpRight, BsTrash, BsDownload, BsSlashCircle } from 'react-icons/bs';

import TransferLeadsToCrmModal from '#crmFeature/components/leads/TransferLeadsToCrmModal/TransferLeadsToCrmModal';
import CustomColumnDialogs from '#customColumnsFeature/components/CustomColumnDialogs/CustomColumnDialogs';
import { useCustomColumnsTable, withCustomColumns } from '#customColumnsFeature/hooks/useCustomColumnsTable';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import ActionScopeButton from '#globalComponents/Table/actions/ActionScopeButton';
import BulkActionsBar from '#globalComponents/Table/actions/BulkActionsBar';
import Table from '#globalComponents/Table/Table';
import TableFilterToolbar from '#globalComponents/Table/TableFilterToolbar';
import { useClampPage } from '#globalHooks/useClampPage';
import { useScopedSelection } from '#globalHooks/useScopedSelection';

import BlacklistSelectionModal from '../../components/BlacklistSelectionModal/BlacklistSelectionModal';
import ContactListEditModal from '../../components/ContactListEditModal/ContactListEditModal';
import { LIST_RECIPIENT_FILTER_SCHEMA, LIST_RECIPIENTS_PAGE_SIZE } from '../../constants/tableFilters';
import { useRowHighlight } from '../../hooks/useRowHighlight';
import { useRemoveCompaniesFromList } from '../../models/mutations';
import { COMPANY_SYSTEM_COLUMNS } from '../systemColumns';

import { buildCompanyColumns } from './companyColumns';

const EMPTY_ROWS = [];

const getCompanyId = (row) => row.crmCompanyId;
const getRecipientRef = (row) => ({ crmCompanyId: row.crmCompanyId });
const getCompanyLabel = (row) => row.crmCompanies?.companyName || 'Firma';
// Both domains, since a company's mail domain often differs from its website. The backend reduces a
// URL or an email to its domain, de-duplicates, and skips freemail domains.
const toBlacklistEntries = (row) =>
	[row.crmCompanies?.websiteUrl, row.crmCompanies?.emailAddress]
		.filter(Boolean)
		.map((value) => ({ entryType: 'domain', value }));

const formatCount = (count) => count.toLocaleString('de-DE');

/**
 * Companies tab of the contact list detail page: company recipients (companies without a
 * contact person) in the same table design as the contacts tab — server-paginated, filterable,
 * with scoped selection, bulk CRM transfer, bulk remove, and per-row remove.
 */
const ContactListCompaniesTab = ({ listId, hasCrm, urlFilters, query, highlightId, onHighlightConsumed }) => {
	const navigate = useNavigate();
	const removeCompaniesMutation = useRemoveCompaniesFromList();
	const { filters, filtersKey, setFilters, page, setPage } = urlFilters;

	const rows = query.data?.companies ?? EMPTY_ROWS;
	const total = query.data?.total ?? 0;
	const listTotal = query.data?.stats?.total ?? 0;
	const totalPages = query.data?.totalPageCount ?? 1;

	const [removeTarget, setRemoveTarget] = useState(null);
	const [blacklistRequest, setBlacklistRequest] = useState(null);
	const [editCompany, setEditCompany] = useState(null);
	const [transferRequest, setTransferRequest] = useState(null);

	useClampPage({ page, totalPages, setPage, isReady: !query.isFetching });

	useRowHighlight({
		listId,
		kind: 'companies',
		highlightId,
		settled: !query.isFetching,
		rows,
		getRowId: getCompanyId,
		urlFilters,
		onConsumed: onHighlightConsumed,
	});

	const selection = useScopedSelection(rows, {
		totalCount: total,
		filters,
		resetKey: `${listId}|${filtersKey}`,
		rowId: getCompanyId,
		entityLabel: { singular: 'Firma', plural: 'Firmen', gender: 'f' },
	});

	const openBlacklist = () => {
		const selectedRows = rows.filter((row) => selection.selectedIds.includes(getCompanyId(row)));
		setBlacklistRequest({
			entries: selectedRows.flatMap(toBlacklistEntries),
			selectedCount: selectedRows.length,
			withoutValueCount: selectedRows.filter((row) => toBlacklistEntries(row).length === 0).length,
		});
	};

	const customColumns = useCustomColumnsTable({
		listId,
		table: 'contactListCompanies',
		rows,
		selection,
		getRecipientRef,
		getRowLabel: getCompanyLabel,
	});

	const columns = useMemo(
		() =>
			withCustomColumns(
				buildCompanyColumns({
					filters,
					onFilterChange: setFilters,
					hasCrm,
					onEdit: setEditCompany,
					onOpenCompany: (companyId) => navigate(`/crm/companies/${companyId}`),
					onRemove: (row) => setRemoveTarget({ scope: { ids: [row.crmCompanyId] }, count: 1 }),
				}),
				[...COMPANY_SYSTEM_COLUMNS, ...customColumns.columns],
			),
		[filters, setFilters, hasCrm, navigate, customColumns.columns],
	);

	const openTransfer = () => setTransferRequest({ scope: selection.toActionScope(), count: selection.actionCount });

	const handleConfirmRemove = async () => {
		if (!removeTarget) return;
		await removeCompaniesMutation.mutateAsync({ listId, scope: removeTarget.scope });
		setRemoveTarget(null);
		selection.clear();
	};

	if (query.isLoading) {
		return <p className="text-sm text-bgBase-800 py-8 text-center">Wird geladen ...</p>;
	}

	if (listTotal === 0) {
		return (
			<div className="flex flex-col items-center gap-3 py-16 text-center">
				<BsBuilding className="w-8 h-8 text-bgBase-800" />
				<h3 className="text-sm font-semibold text-text">Keine Firmen in dieser Liste</h3>
				<p className="text-xs text-bgBase-800 max-w-sm">
					Firmen ohne Ansprechpartner erscheinen hier — fügen Sie sie über die Lead-Übersicht hinzu.
				</p>
			</div>
		);
	}

	const removeCount = removeTarget?.count ?? 0;

	return (
		<>
			<CustomColumnDialogs dialogs={customColumns.dialogs} scopeLabel={selection.actionScopeLabel} />
			<TableFilterToolbar
				urlFilters={urlFilters}
				schema={LIST_RECIPIENT_FILTER_SCHEMA}
				searchPlaceholder="Firmenname oder E-Mail suchen …"
				resultLabel={`${formatCount(total)} von ${formatCount(listTotal)} Firmen`}
			/>
			<Table
				rows={rows}
				columns={columns}
				rowId={getCompanyId}
				layoutKey="contactListCompanies"
				onAddColumn={customColumns.dialogs.openCreate}
				addColumnLabel="Eigene Spalte hinzufügen"
				highlightedId={highlightId ?? undefined}
				emptyLabel="Keine Firmen entsprechen den aktiven Filtern"
				getRowProps={(row) => ({ id: `list-row-${row.crmCompanyId}` })}
				selection={selection.tableSelection}
				actions={{
					label: selection.actionScopeLabel,
					buttons: (
						<>
							<ActionScopeButton
								icon={<BsDownload />}
								onClick={customColumns.dialogs.openExport}
								disabled={selection.actionCount === 0}
							>
								CSV-Export
							</ActionScopeButton>
							{hasCrm && (
								<ActionScopeButton
									icon={<BsBoxArrowUpRight />}
									onClick={openTransfer}
									disabled={selection.actionCount === 0}
								>
									Als Deal anlegen
								</ActionScopeButton>
							)}
						</>
					),
				}}
				pagination={{
					currentPage: page,
					totalPages,
					onPageChange: setPage,
					totalItems: total,
					pageSize: LIST_RECIPIENTS_PAGE_SIZE,
				}}
				bulkBar={
					<BulkActionsBar
						selectedCount={selection.count}
						entityLabel={selection.entityLabel}
						onClear={selection.clear}
						actions={[
							{
								key: 'blacklist',
								icon: <BsSlashCircle />,
								label: 'Auf Blacklist setzen',
								disabled: selection.selectAllInFilter,
								title: selection.selectAllInFilter ? 'Nur für einzeln ausgewählte Einträge' : undefined,
								onClick: openBlacklist,
							},
							{
								key: 'remove',
								icon: <BsTrash />,
								label: 'Entfernen',
								onClick: () => setRemoveTarget({ scope: selection.toScope(), count: selection.count }),
							},
						]}
					/>
				}
			/>

			<ConfirmModal
				isOpen={Boolean(removeTarget)}
				onClose={() => setRemoveTarget(null)}
				onConfirm={handleConfirmRemove}
				title={removeCount === 1 ? 'Firma entfernen' : `${formatCount(removeCount)} Firmen entfernen`}
				message={
					removeCount === 1
						? 'Möchten Sie diesen Firmen-Empfänger aus der Liste entfernen?'
						: `Möchten Sie ${formatCount(removeCount)} Firmen-Empfänger aus der Liste entfernen?`
				}
				confirmText="Entfernen"
				cancelText="Abbrechen"
				isLoading={removeCompaniesMutation.isPending}
			/>

			{/* Company recipients have no contact, so only the company tab shows */}
			{blacklistRequest && (
				<BlacklistSelectionModal
					entryType="domain"
					entries={blacklistRequest.entries}
					selectedCount={blacklistRequest.selectedCount}
					withoutValueCount={blacklistRequest.withoutValueCount}
					onClose={() => setBlacklistRequest(null)}
					onDone={selection.clear}
				/>
			)}

			<ContactListEditModal
				isOpen={Boolean(editCompany)}
				onClose={() => setEditCompany(null)}
				company={editCompany}
				initialTab="company"
				onSaveSuccess={() => query.refetch()}
			/>

			{hasCrm && transferRequest && (
				<TransferLeadsToCrmModal
					isOpen
					onClose={() => setTransferRequest(null)}
					tableScope={{ table: 'contactListCompanies', contextId: listId, ...transferRequest.scope }}
					count={transferRequest.count}
				/>
			)}
		</>
	);
};

ContactListCompaniesTab.propTypes = {
	listId: PropTypes.string.isRequired,
	hasCrm: PropTypes.bool,
	// Result of useUrlFilters(LIST_RECIPIENT_FILTER_SCHEMA, { namespace: 'companies' }).
	urlFilters: PropTypes.object.isRequired,
	// useContactListCompanies result for the current page + filters.
	query: PropTypes.shape({
		data: PropTypes.object,
		isLoading: PropTypes.bool,
		isFetching: PropTypes.bool,
		refetch: PropTypes.func.isRequired,
	}).isRequired,
	highlightId: PropTypes.string,
	onHighlightConsumed: PropTypes.func.isRequired,
};

export default ContactListCompaniesTab;
